import { useEffect, useState } from 'react';
import { toast } from '../../../../../ui/toast';
import {
  getNextAssessment,
  submitAnswer,
  WellnessAnswer,
} from '../../../../../utils/api_service/wellness';
import type { WellnessNextAssessmentResponse } from '../../../../../utils/types/wellness_types';
import { getTypeLabel, getOptionLabels } from '../constants';
import type { IAssessmentTypeLabel } from '../constants';

export type WellnessModalState = 'loading' | 'intro' | 'questions' | 'transition' | 'completed' | 'error';

export interface IUseWellnessAssessmentReturn {
  state: WellnessModalState;
  current: WellnessNextAssessmentResponse | null;
  nextUp: WellnessNextAssessmentResponse | null;
  answers: Record<string, number | null>;
  submitting: boolean;
  allAnswered: boolean;
  label: IAssessmentTypeLabel | null;
  options: string[];
  answeredCount: number;
  totalQuestions: number;
  handleStart: () => void;
  handleSelect: (questionId: string, value: number) => void;
  handleSubmit: () => void;
  handleContinue: () => void;
}

/**
 * Encapsulates the entire assessment-flow lifecycle: fetch → intro → questions → submit → transition → complete.
 * Pure state + side-effects; no rendering logic.
 */
export const useWellnessAssessment = (
  inviteId: string,
  isOpen: boolean,
  onProgress?: () => void,
): IUseWellnessAssessmentReturn => {
  const [state, setState] = useState<WellnessModalState>('loading');
  const [current, setCurrent] = useState<WellnessNextAssessmentResponse | null>(null);
  const [nextUp, setNextUp] = useState<WellnessNextAssessmentResponse | null>(null);
  const [answers, setAnswers] = useState<Record<string, number | null>>({});
  const [submitting, setSubmitting] = useState(false);

  // Fresh start every time the modal opens
  useEffect(() => {
    if (!isOpen) return;

    setState('loading');
    setCurrent(null);
    setAnswers({});

    getNextAssessment(inviteId)
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data: WellnessNextAssessmentResponse) => {
        if (data.completed) {
          setState('completed');
          return;
        }
        setCurrent(data);
        setAnswers(
          Object.fromEntries((data.questions ?? []).map((q) => [q.questionId, q.choiceAnswer ?? null])),
        );
        setState('intro');
      })
      .catch(() => {
        setState('error');
      });
  }, [isOpen, inviteId]);

  const handleStart = () => setState('questions');

  const handleSelect = (questionId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const allAnswered = current?.questions?.every((q) => answers[q.questionId] !== null) ?? false;

  const handleSubmit = () => {
    if (!current?.attemptId || submitting) return;

    setSubmitting(true);
    const payload: WellnessAnswer[] = Object.entries(answers).map(([questionId, choiceAnswer]) => ({
      questionId,
      choiceAnswer: choiceAnswer as number,
    }));

    submitAnswer({ attemptId: current.attemptId, answers: payload })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data: WellnessNextAssessmentResponse) => {
        onProgress?.();
        if (data.completed) {
          setState('completed');
          return;
        }
        setNextUp(data);
        setState('transition');
      })
      .catch(() => {
        toast.error('Could not submit your answers. Please try again.', { position: 'bottom-center' });
      })
      .finally(() => setSubmitting(false));
  };

  const handleContinue = () => {
    if (!nextUp) return;
    setCurrent(nextUp);
    setAnswers(
      Object.fromEntries((nextUp.questions ?? []).map((q) => [q.questionId, q.choiceAnswer ?? null])),
    );
    setNextUp(null);
    setState('intro');
  };

  const label = current ? getTypeLabel(current.type ?? '') : null;
  const options = current ? getOptionLabels(current.type ?? '') : [];
  const answeredCount = Object.values(answers).filter((v) => v !== null).length;
  const totalQuestions = current?.questions?.length ?? 0;

  return {
    state,
    current,
    nextUp,
    answers,
    submitting,
    allAnswered,
    label,
    options,
    answeredCount,
    totalQuestions,
    handleStart,
    handleSelect,
    handleSubmit,
    handleContinue,
  };
};
